import React, { useEffect, useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { ReactSVG } from "react-svg";
import paths from '../../consts/paths';
import Audio from './Audio';
import exit from './assets/icon_exit_white.svg';
import muteOnIcon from './assets/icon_mute_on.svg';
import muteOffIcon from './assets/icon_mute_off.svg';
import arrow from './assets/icon_arrow.svg';
import SlideManager from './SlideManager';
import RoundUpAPI from '../../api/RoundupApi';
import GrownUpAPI from '../../api/GrownUpAPI';
import preloadAsset from '../../utils/AssetUtils';
import { colorChoices } from '../../utils/RoundUpUtils';
import audioTrack from './assets/practice_makes_perfect.mp3';
import JWTManager from '../../utils/JWTManager';
import BugsnagClient from '../../utils/BugsnagClient';
import WindowUtils from '../../utils/WindowUtils';
import ComingSoonSlide from './slides/OtherSlides/ComingSoonSlide';
import ErrorSlide from './slides/OtherSlides/ErrorSlide';
import LoadingSlide from './slides/OtherSlides/LoadingSlide';

const RoundUp = () => {
  const token = JWTManager.getToken();

  if (!token) return <Redirect to={`${paths.SIGN_IN}?previousPath=${location.pathname + location.search}`} />

  const releaseLatestVersion = process.env.REACT_APP_PHASE_5_ROUNDUP === 'true';
  const enableComingSoon = !releaseLatestVersion;

  const [data, setData] = useState(null);
  const [roundUpId, setRoundUpId] = useState(null);
  const [grownUpId, setGrownUpId] = useState(null);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [firstPlay, setFirstPlay] = useState(true);
  const [activeBackgroundColor, setActiveBackgroundColor] = useState('');
  const [displayDate, setDisplayDate] = useState(null);
  const [isAndroid, setIsAndroid] = useState(false);
  
  const [dataLoading, setDataLoading] = useState(true);
  const [trackingId, setTrackingId] = useState(null);
  const [trackingSetUpLoading, setTrackingSetUpLoading] = useState(false);
  const [lastTrackingSlideIndex, setLastTrackingSlideIndex] = useState(null);
  const [slideTrackingData, setSlideTrackingData] = useState(null);

  const [playAudioTrack, setPlayAudioTrack] = useState(false);
  const [pauseAudioTrack, setPauseAudioTrack] = useState(false)
  const [fadeAudioTrack, setFadeAudioTrack] = useState(false);
  const [mutedAudioTrack, setMutedAudioTrack] = useState(true);
  const { kid_id } = useParams();

  const showSlides = data && grownUpId && kid_id;

  useEffect(() => {
    // PRELOAD ASSETS
    preloadAsset(exit);
    preloadAsset(muteOnIcon);
    preloadAsset(muteOffIcon);

    preloadAsset(audioTrack, true);
  }, [])

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      setIsAndroid(true);
    }
  }, []);

  useEffect(() => {
    if (data) {
      const slideColor = data[index].colorOption;
      if (slideColor !== activeBackgroundColor) {
        setActiveBackgroundColor(colorChoices[slideColor].backgroundColor);
      }
    }
  }, [data, index])

  // TRACKING - setup
  const trackingSetUp = async () => {
    const getTrackingId = await RoundUpAPI.postRoundUpViewing(slideTrackingData, grownUpId, roundUpId)
    setTrackingId(getTrackingId.id);
    setTrackingSetUpLoading(false);
  }

  // TRACKING - update (slide changed)
  const trackingUpdate = async (slide_designator, slide_number) => {
    return RoundUpAPI.updateRoundUpViewing(trackingId, slide_designator, slide_number)
  }

  const triggerTracking = () => {
    // only trigger tracking if slide is higher than tracking already recorded
    if (!lastTrackingSlideIndex || index > lastTrackingSlideIndex) {
      const { slide_designators } = slideTrackingData;
      const slideName = slide_designators[index + 1];
      if (slideName) {
        trackingUpdate(slideName, index + 2) // plus 2 as BE not using index value here
        setLastTrackingSlideIndex(index);
      }
    }
  }

  // CONTENT DATA - get
  const getRoundupData = async () => {
    return new Promise((resolve) => {
      RoundUpAPI.getRoundUp(kid_id)
      .then((response) => {
        if (response) {
          const { id, slides, tracking, roundUpDateDisplay } = response;
          setRoundUpId(id);
          setData(slides);
          setSlideTrackingData(tracking)
          setDisplayDate(roundUpDateDisplay)
        }
        setDataLoading(false)
        resolve();
      })
      .catch(error => {
        // This is here to try and track down some suspicious looking
        // 403 Errors we are getting in production
        if (BugsnagClient?.notify) {
          BugsnagClient.notify(error, {
            metaData: {
              props: {
                grownUpId,
                kid_id
              }
            }
          });
        }
        setDataLoading(false)
      })
    })
  }

  // HIDE CHAT WIDGET
  useEffect(() => {
    // Create a new mutation observer
    /* eslint-disable-next-line */
    const observer = new MutationObserver(() => {
      const launcher = document.getElementById('launcher');
      const widget = launcher?.parentNode;
      if (widget) {
        widget.style.cssText = 'display: none';
        // Once the widget is found and hidden, we can stop observing for changes
        observer.disconnect();
      }
    });
    // Start observing the document with the configured parameters
    observer.observe(document, { childList: true, subtree: true });
    // Clean up the observer when the component is unmounted
    return () => observer.disconnect();
  }, []);  

  // EXIT - redirect to timeline
  const triggerClose = () => {
    WindowUtils.replaceLocation(paths.TIMELINE(kid_id))
  }

  const fetchGrownUpFromToken = async () => {
    if (!grownUpId) {
      return new Promise((resolve, reject) => {
        GrownUpAPI.fetchGrownUpFromToken()
        .then((response) => {
          const { id } = response.data.data;
          setGrownUpId(id);
          setDataLoading(false)
          resolve();
        })
        .catch(error => {
          reject(error)
          setDataLoading(false)
        })
      })
    }
    setDataLoading(false)
    return null;
  }

  useEffect(() => {
    setDataLoading(true)
    fetchGrownUpFromToken()
  }, [grownUpId])

  // Fetch the round-up data
  useEffect(() => {
    if (!data && kid_id && grownUpId) {
      setDataLoading(true)
      getRoundupData()
    }
  }, [data, kid_id, grownUpId])

  // Set up tracking
  useEffect(() => {
    if (data) {
      // setSlideTotal(data.length);
      if (!trackingId && !trackingSetUpLoading && slideTrackingData && grownUpId && roundUpId) {
        setTrackingSetUpLoading(true)
        trackingSetUp()
      }
    }
  }, [data, trackingId, grownUpId, roundUpId, slideTrackingData, trackingSetUpLoading])

  // AUDIO
  const onToggleSound = () => {
    setMutedAudioTrack(!mutedAudioTrack)
  }

  const triggerAudioTrackPause = (value) => {
    setPauseAudioTrack(value)
  }
  const changeSlide = (newIndex) => {
    setVisible(false);
    setTimeout(() => {
      setIndex(newIndex);
      setVisible(true);
      if (newIndex === (data.length - 1) && !mutedAudioTrack) {
        setFadeAudioTrack(true);
      }

      if (newIndex > lastTrackingSlideIndex) {
        triggerTracking();
      }
    }, 700); // Matches exit animation duration
  }; 

  // START - initial cta clicked
  const onStartFunc = () => {
    setPlayAudioTrack(true);
    if (firstPlay) {
      setMutedAudioTrack(false);
      setFirstPlay(false);
    }
    changeSlide(1);
  }

  // KEYBOARD - controls for back and forth
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && index > 0) {
        changeSlide(Math.min(index +1, data.length -1))
      } else if (e.key === 'ArrowLeft' && index > 0) {
        changeSlide(Math.max(index - 1, 0))
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, data]);

  // SWIPING - control for back and forth
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (index > 0) changeSlide( Math.min(index +1, data.length -1))
    },
    onSwipedRight: () => {
      changeSlide(Math.max(index - 1, 0))
    },
    trackMouse: true,
    preventDefaultTouchmoveEvent: true
  });

  // REFETCH - is triggered by user if they are on the error view
  const onRetry = () => {
    if (!grownUpId) {
      setDataLoading(true)
      fetchGrownUpFromToken()
    }

    if (kid_id) {
      setDataLoading(true)
      getRoundupData()
    }
  }

  const renderHeaderControls = () => {
    let muteClass = 'Roundup__controls__mute';
    if (firstPlay) {
      muteClass = 'Roundup__controls__hidden';
    }
    let assetBackgroundColor = '#EC660D';
    if (data) {
      const { colorOption: selected } = data[index];
      const { assetBackgroundColor: background } = colorChoices[selected];
      assetBackgroundColor = background;
    }

    return (
      <div className='Roundup__controls'>
        <button 
          type='button'
          aria-label='exit'
          onClick={triggerClose}
          className='Roundup__controls__exit'
          style={{ backgroundColor: assetBackgroundColor }}
        >
          <ReactSVG src={exit} />
        </button>
        <button
          type='button'
          onClick={onToggleSound}
          aria-label='mute_button'
          className={muteClass}
          style={{ backgroundColor: assetBackgroundColor }}
        >
          <ReactSVG src={mutedAudioTrack ? muteOnIcon : muteOffIcon} />
        </button>
      </div>
    )
  }

  const renderNavControls = () => {
    const next = Math.min(index +1, data.length -1);
    const prev = Math.max(index - 1, 0);
    return (
      <div
        className="Roundup__nav"
        style={{
          bottom: isAndroid ? '70px' : 0
        }}
      >
        <button
          type="button"
          onClick={() => changeSlide(prev)}
          disabled={index === 0}
          hidden={index === data.length - 1}
          className='Roundup__nav__prev'
        >
          <ReactSVG src={arrow} />
        </button>
        <button
          type="button"
          onClick={() => changeSlide(next)}
          disabled={index === data.length - 1}
          hidden={index === data.length - 1}
          className='Roundup__nav__next'
        >
          <ReactSVG src={arrow} />
        </button>
      </div>
    )
  }
  
  return (
    <div className='Roundup__page'>
      <div
        className='Roundup'
        {...swipeHandlers}
        style={{
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: activeBackgroundColor
        }}
      > 
        <div className='Roundup__content'>
          {renderHeaderControls()}
          {!enableComingSoon && audioTrack && showSlides && (
            <Audio
              play={playAudioTrack}
              track={audioTrack}
              fade={fadeAudioTrack}
              muted={mutedAudioTrack}
              pause={pauseAudioTrack}
            />
            )}
          {!releaseLatestVersion && displayDate &&  <ComingSoonSlide data={{ date: displayDate }} />}
          {releaseLatestVersion && !dataLoading && !data && <ErrorSlide refreshFunc={onRetry} /> }
          {releaseLatestVersion && dataLoading && <LoadingSlide color={activeBackgroundColor} /> }
          {!dataLoading && releaseLatestVersion && data && (
            <SlideManager
              slides={data}
              kidId={kid_id}
              grownUpId={grownUpId}
              startFunc={onStartFunc}
              index={index}
              visible={visible}
              triggerAudioTrackPause={triggerAudioTrackPause}
              triggerClose={triggerClose}
              isAndroid={isAndroid}
            />
          )}
          {index > 0 && renderNavControls()}
        </div>
      </div>
    </div>
  )
  
}

export default RoundUp;
